import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Popover, Select } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import asyncErrorHandler from 'src/utils/asyncErrorHandler';
import { getCurrentCustomer } from 'src/utils/localStorage';
import UserAvatarPopover from 'src/components/UserAvatarPopover';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { UserResponse } from 'src/types';

interface RequestFollowersGroupProps {
  className?: string;
  maxCount?: number;
  followers: any[];
  requestUuid?: string;
  shape?: 'circle' | 'square';
  editable?: boolean;
  setFollowers?: (followers: any[]) => void;
}

export const RequestFollowersGroup = ({
  className,
  maxCount = 3,
  followers,
  requestUuid,
  shape = 'square',
  editable = false,
  setFollowers,
}: RequestFollowersGroupProps) => {
  const user = useSelector(
    (store: RootState) => store.auth.user
  ) as UserResponse;

  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [contactsData, setContactsData] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const navigate = useNavigate();
  const selectRef = useRef<any>(null);

  useEffect(() => {
    if (addOpen) {
      const timer = setTimeout(() => {
        selectRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [addOpen]);

  const fetchContacts = async () => {
    if (contactsData.length > 0) return;
    setContactsLoading(true);
    try {
      const currentCustomer = getCurrentCustomer();
      const res = await apiRequests.get(apiRoutes.CONTACTS, {
        'filters[billing_contacts.status][]': 'active',
        'filters[customer.uuid][]': currentCustomer.uuid,
        'filters[type][]': 'contact',
        'page[size]': 500,
      });
      setContactsData(res.data.data);
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setContactsLoading(true);
      setContactsLoading(false);
    }
  };

  const handleAdd = async (contact: any) => {
    const userUuid = contact.user?.uuid;
    const previousState = [...followers];

    const newFollower = {
      ...contact,
      uuid: userUuid, 
      user: contact.user,
      pivot: { type: 'follower' }
    };

    setFollowers?.([...followers, newFollower]);
    setSearch('');
    
    try {
      await apiRequests.post(`${apiRoutes.PROJECT_REQUEST}/${requestUuid}/followers`, {
        user_id: contact.user.uuid,
      });
    } catch (error) {
      setFollowers?.(previousState);
      asyncErrorHandler(error);
    }
  };

  const handleRemove = async (userUuid: string) => {
    const previousState = [...followers];
    setFollowers?.(followers.filter((f) => f.uuid !== userUuid));

    try {
      await apiRequests.delete(`${apiRoutes.PROJECT_REQUEST}/${requestUuid}/followers/${userUuid}`);
      
      if (userUuid === user?.uuid) {
        navigate('/dashboard'); 
      }
    } catch (error) {
      setFollowers?.(previousState);
      asyncErrorHandler(error);
    }
  };

  const followerUserUuids = useMemo(() => {
    return new Set(followers.map((f) => f.user?.uuid || f.uuid));
  }, [followers]);

  const options = useMemo(() => {
    return contactsData
      .filter((contact: any) => {
        const userUuid = contact.user?.uuid;
        return userUuid && !followerUserUuids.has(userUuid);
      })
      .filter((contact: any) =>
        !search || contact.name?.toLowerCase().includes(search.toLowerCase())
      )
      .map((contact: any) => ({ 
        label: contact.name, 
        value: contact.uuid,
        contact 
      }));
  }, [contactsData, followerUserUuids, search]);

  return (
    <div className={`flex items-center gap-1 ${className ?? ''}`}>
      {editable && (
        <Popover
          open={addOpen}
          trigger="click"
          placement="bottomLeft"
          onOpenChange={(open) => {
            setAddOpen(open);
            if (open) fetchContacts();
            else setSearch('');
          }}
          content={
            <div style={{ width: 250 }}>
              <Select
                ref={selectRef}
                autoFocus
                showSearch
                open
                mode="multiple" 
                style={{ width: '100%' }}
                placeholder="Search and add..."
                filterOption={false}
                loading={contactsLoading}
                onSearch={setSearch}
                searchValue={search}
                value={[]} 
                options={options}
                notFoundContent={
                  <p style={{ padding: '4px 8px' }}>No contacts found</p>
                }
                onSelect={(_value, option: any) => {
                  handleAdd(option.contact)
                  setSearch(''); 
                }}
                autoClearSearchValue={true}
                maxTagCount={0}
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </div>
          }
        >
          <button
            className="flex items-center justify-center text-blue-400 hover:text-blue-600 transition-colors"
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <PlusCircleOutlined style={{ fontSize: 20 }} />
          </button>
        </Popover>
      )}

      {followers.length > 0 && (
        <Avatar.Group maxCount={maxCount}>
          {followers.map((follower: any) => (
            <UserAvatarPopover
              editable={editable}
              key={follower.uuid}
              alt={follower.name}
              user={follower}
              shape={shape}
              onRemove={() => handleRemove(follower.uuid)}
            />
          ))}
        </Avatar.Group>
      )}
    </div>
  );
};